import * as React from 'react';
import { useAd } from '../hooks/ad';
import { Native } from './native/native';
import { Banner } from './banner';
import { Placeholder } from './placeholder';
import { Fallback } from './fallback';
import { isNative } from '../utils/ast';
import { isTakeoverV2 } from '../utils/takeover';
import { Takeover } from './takeover';
import {
    DeviceType,
    Vertical,
    Subvertical,
    PageType,
    Features,
    BannerAdObj,
} from '../../shared/types/ast';
import { useCachedAdPlaceholder } from '../hooks/cached-ad-placeholder';
import { ErrorBoundary } from './error-boundary';
import '../podlet/types';
import { displayAdsLog } from '../utils/debug';

export type Props = {
    id: string;
    className?: string;
    grid?: boolean;
    vertical?: Vertical;
    subvertical?: Subvertical;
    deviceType: DeviceType;
    pageType?: PageType;
    features?: Features;
};

function AdSwitch(props: Props) {
    const {
        id,
        className,
        grid = false,
        vertical,
        subvertical,
        deviceType,
        pageType = 'result',
        features,
    } = props;
    const { adObj, status, targetId } = useAd(id, false);
    const cachedAdPlaceholder = useCachedAdPlaceholder(
        id,
        targetId,
        pageType,
        status,
        vertical === 'travel',
    );
    displayAdsLog('------- switch.tsx status');
    displayAdsLog(`${status} ${targetId}`);

    if (
        cachedAdPlaceholder.loadCachedFallbackAd &&
        cachedAdPlaceholder.adCache &&
        cachedAdPlaceholder.CachedPlaceholderElement
    )
        return cachedAdPlaceholder.CachedPlaceholderElement;
    if (adObj) {
        if (isNative(adObj)) {
            return (
                <Native
                    adObj={adObj}
                    id={targetId}
                    className={className}
                    grid={grid}
                    vertical={vertical}
                    subvertical={subvertical}
                    status={status}
                    pageType={pageType}
                    deviceType={deviceType}
                    features={features}
                />
            );
        } else if (isTakeoverV2(adObj, deviceType)) {
            return (
                <Takeover id={id} targetId={targetId} className={className} />
            );
        } else {
            const _adObj = adObj as BannerAdObj;
            return (
                <Banner
                    id={id}
                    grid={grid}
                    targetId={targetId}
                    pageType={pageType}
                    vertical={vertical}
                    className={className}
                    adSize={
                        _adObj?.height && _adObj?.width
                            ? {
                                  height: _adObj.height,
                                  width: _adObj.width,
                              }
                            : null
                    }
                />
            );
        }
    }

    if (status === 'failed') {
        return null;
    }

    if (
        cachedAdPlaceholder.adCache &&
        cachedAdPlaceholder.CachedPlaceholderElement
    )
        return cachedAdPlaceholder.CachedPlaceholderElement;

    return (
        <Placeholder id={targetId} className={className}>
            <Fallback id={id} deviceType={deviceType} />
        </Placeholder>
    );
}

export const Switch = (props: Props) => {
    return (
        <ErrorBoundary fallback={null}>
            <AdSwitch {...props} />
        </ErrorBoundary>
    );
};
